import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiTrustWishShow, apiTrustWishVerify, apiTrustWishResendOtp } from "../lib/api";

export default function TrustWishVerify() {
  // ✅ Match router param name (:id)
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const idNum = Number(id);
  const idValid = Number.isFinite(idNum) && idNum > 0;

  const [code, setCode] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!idValid) return;

    (async () => {
      try {
        const r = await apiTrustWishShow(idNum);
        if (r?.wish?.otp_verified_at) {
          // basename="/app" is set in Vite; routes should be app-relative only
          nav(`/trusts/wishes/${idNum}`, { replace: true });
        }
      } catch {
        // ignore — user can still enter OTP
      }
    })();
  }, [idNum, idValid, nav]);

  // If URL is wrong, show a friendly error (prevents NaN calls)
  if (!idValid) {
    return (
      <div className="section-box max-w-sm mx-auto p-4 sm:p-6">
        <div className="alert-danger">Invalid wish ID in URL.</div>
      </div>
    );
  }

  async function onVerify() {
    setErr(null);
    setInfo(null);
    if (code.length !== 6) return setErr("Enter the 6-digit code");
    setLoading(true);
    try {
      const r = await apiTrustWishVerify(idNum, { code });
      if (!r?.ok) throw new Error(r?.error || "Failed to verify code");
      nav(`/trusts/wishes/${idNum}`, { replace: true });
    } catch (e: any) {
      setErr(e?.message || "Could not verify code");
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    setErr(null);
    setInfo(null);
    try {
      await apiTrustWishResendOtp(idNum); // ✅ will be a real number now
      setInfo("New code sent.");
    } catch {
      setErr("Could not resend code. Try again.");
    }
  }

  return (
    <div className="section-box max-w-sm mx-auto p-4 sm:p-6">
      <h1 className="text-xl font-semibold mb-2">Confirm Your Wish</h1>
      <p className="text-sm text-gray-600 mb-4">
        We sent a 6-digit code to your email. Enter it below.
      </p>

      {err && <div className="alert-danger mb-3">{err}</div>}
      {info && <div className="alert-success mb-3">{info}</div>}

      <div className="space-y-4">
        <input
          className={[
            "input w-full text-center text-2xl tracking-widest",
            "rounded-xl border border-gray-300 px-3 py-3",
            "focus:outline-none focus:ring-2 focus:ring-blue-500",
          ].join(" ")}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="••••••"
          disabled={loading}
        />

        <div className="flex gap-2">
          <button className="btn btn-success w-auto px-4" onClick={onVerify} disabled={loading}>
            {loading ? "Verifying…" : "Verify"}
          </button>
          <button className="btn btn-white w-auto px-4" onClick={onResend} disabled={loading}>
            Resend code
          </button>
        </div>
      </div>
    </div>
  );
}
